import { isBuildingGeomTag, tagBuildingFace } from "building-types/buildingGeomTag/buildingGeomTags";
import type {
  BuildingGeomCondition,
  BuildingGeomOperation,
  BuildingGeomParent,
  BuildingGeomPosition,
  BuildingGeomTag,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import {
  TAG_POSITION_END,
  TAG_POSITION_START,
  TAG_POSITION_BOTTOM,
  TAG_POSITION_TOP,
  TAG_POSITION_RIGHT,
  TAG_POSITION_LEFT,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { WallSection } from "building-types/walls/wallDesciptorTypes";
import { StructuralAssemblyType, WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { SolidWallLayer, WallMass } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { roomProducesInteriorFaces } from "doc/rooms/roomQuery";
import type { Face } from "geom/brep";
import { faceAABB3, faceCopy, tagFace, tagFaces } from "geom/brep";
import { wallMassLayers } from "./wallLayers";

export function wallFacesNew(
  leftFaces: Face[] = [],
  rightFaces: Face[] = [],
  topFaces: Face[] = [],
  bottomFaces: Face[] = [],
  startFaces: Face[] = [],
  endFaces: Face[] = [],
  leftHoleFaces: Face[] = [],
  rightHoleFaces: Face[] = [],
  leftExteriorFaces: Face[] = [],
  rightExteriorFaces: Face[] = [],
): WallFaces {
  return {
    leftFaces,
    rightFaces,
    topFaces,
    bottomFaces,
    startFaces,
    endFaces,
    leftHoleFaces,
    rightHoleFaces,
    leftExteriorFaces,
    rightExteriorFaces,
  };
}

export function wallFacesOpposite(from: WallFaces): WallFaces {
  return {
    topFaces: from.topFaces,
    bottomFaces: from.bottomFaces,
    leftFaces: from.rightFaces,
    leftExteriorFaces: from.rightExteriorFaces,
    leftHoleFaces: from.rightHoleFaces,
    rightFaces: from.leftFaces,
    rightExteriorFaces: from.leftExteriorFaces,
    rightHoleFaces: from.leftHoleFaces,
    startFaces: from.endFaces,
    endFaces: from.startFaces,
  };
}

const OPERATION_WALL_FACE_FROM_SINGLE_FACE: BuildingGeomOperation = {
  type: "OPERATION",
  operation: "WALL_FACE_FROM_SINGLE_FACE",
};

function wallFacesFromSingleFaces(
  parents: BuildingGeomParent[],
  leftFace: Face,
  rightFace: Face,
  startFace: Face | null = null,
  endFace: Face | null = null,
  topFace: Face | null = null,
  bottomFace: Face | null = null,
): WallFaces {
  function tag(face: Face | null, position: BuildingGeomPosition) {
    if (!face) {
      return [];
    }
    return [tagBuildingFace(faceCopy(face), [...parents, OPERATION_WALL_FACE_FROM_SINGLE_FACE, position])];
  }

  return wallFacesNew(
    tag(leftFace, TAG_POSITION_LEFT),
    tag(rightFace, TAG_POSITION_RIGHT),
    tag(topFace, TAG_POSITION_TOP),
    tag(bottomFace, TAG_POSITION_BOTTOM),
    tag(startFace, TAG_POSITION_START),
    tag(endFace, TAG_POSITION_END),
  );
}

/**
 * wallFacesFromWallMass will add all layer faces from a SolidWallMass to a WallFaces
 * object, extracting the appropriate faces from the layers.
 */
export function wallFacesFromWallMass(wallMass: WallMass, wallLayer?: WallLayerType, wallLOD?: WallLOD): WallFaces {
  const { layers, wallType, id, path } = wallMass;

  if (!layers || !wallLayer) {
    if (wallType === WallType.SOLID_MASS) {
      const rightTags: BuildingGeomTag[] = [];

      if (path.rightRoom) {
        rightTags.push({ type: "PARENT", parentId: path.rightRoom.id, parentType: "ROOM" });
      } else if (path.leftRoom) {
        rightTags.push({ type: "PARENT", parentId: path.leftRoom.id, parentType: "ROOM" });
      }

      const { leftFace, rightFace, startFace, endFace, bottomFace, topFace } = wallMass; // update to add any existing tags from wallMass
      const wallFaces = wallFacesFromSingleFaces(
        [{ type: "PARENT", parentId: id, parentType: "WALL_MASS" }],
        leftFace,
        rightFace,
        startFace,
        endFace,
        topFace,
        bottomFace,
      );
      wallFaces.rightFaces = tagFaces(wallFaces.rightFaces, rightTags); //to everyone, not just right faces
      return wallFaces;
    } else {
      return wallFacesNew();
    }
  }

  const wallFaces = wallFacesNew();
  const wallLayers = wallMassLayers(wallMass, wallLayer, wallLOD);
  let bottomFaces: WallFaces | undefined;
  let topFaces: WallFaces | undefined;

  for (let i = 0, l = wallLayers.length; i < l; i++) {
    const layer = wallLayers[i];
    const layerFaces = wallFacesFromWallMass(layer);
    const layerSection = layer.section;

    let leftFaces = true;
    let rightFaces = true;

    if (wallLayer === WallLayerType.SUBSTRATE || wallLayer === WallLayerType.FINISH) {
      leftFaces = layerSection.leftSide.cladding !== WallCladdingType.NONE;
      rightFaces = layerSection.rightSide.cladding !== WallCladdingType.NONE;
    } else if (wallLayer === WallLayerType.STRUCTURE) {
      leftFaces = layerSection.leftSide.structure !== StructuralAssemblyType.NONE;
      rightFaces = layerSection.rightSide.structure !== StructuralAssemblyType.NONE;
    }

    if (!rightFaces && !leftFaces) {
      continue;
    }

    topFaces = layerFaces;
    if (!bottomFaces) {
      bottomFaces = layerFaces;
    }

    mergeWallFaces(wallFaces, layerFaces, {
      leftFaces,
      rightFaces,
      startFaces: true,
      endFaces: true,
    });
  }

  if (bottomFaces) {
    mergeWallFaces(wallFaces, bottomFaces, { bottomFaces: true });
  }

  if (topFaces) {
    mergeWallFaces(wallFaces, topFaces, { topFaces: true });
  }

  return wallFaces;
}

export function mergeWallFaces(target: WallFaces, source: WallFaces, faces?: { [K in keyof WallFaces]?: boolean }) {
  for (const key in source) {
    const faceType = key as keyof WallFaces;
    if (!faces || faces[faceType]) {
      target[faceType].push(...source[faceType]);
    }
  }
}

export type ReplaceWallFacesOperation = "REPLACE_ALL" | "REPLACE_IF_TARGET_EXISTS" | "REPLACE_IF_SOURCE_EXISTS";

export function replaceWallFaces(
  target: WallFaces,
  source: WallFaces,
  faces?: { [K in keyof WallFaces]?: boolean },
  mergeType: ReplaceWallFacesOperation = "REPLACE_ALL",
): WallFaces {
  for (const key in source) {
    const faceType = key as keyof WallFaces;
    if (!faces || faces[faceType]) {
      switch (mergeType) {
        case "REPLACE_ALL": {
          target[faceType] = [...source[faceType]];
          break;
        }
        case "REPLACE_IF_TARGET_EXISTS": {
          target[faceType] = target[faceType]?.length ? [...source[faceType]] : target[faceType];
          break;
        }
        case "REPLACE_IF_SOURCE_EXISTS": {
          target[faceType] = source[faceType]?.length ? [...source[faceType]] : target[faceType];
          break;
        }
        default: {
          assertUnreachable(mergeType);
        }
      }
    }
  }
  return target;
}

export function mapWallFacesToParentId(faces: Face[]) {
  return faces.reduce(facesByParentId, {} as { [parentId: string]: Face[] });
}

export function facesByParentId(map: { [parentId: string]: Face[] }, face: Face) {
  if (!face.tags) {
    return map;
  }
  const seen: Set<string> = new Set();
  face.tags.forEach((tag) => {
    if (!isBuildingGeomTag(tag)) {
      return;
    }
    if (tag.type !== "PARENT") {
      return;
    }
    if (seen.has(tag.parentId)) {
      return;
    }
    if (!map[tag.parentId]) {
      map[tag.parentId] = [];
    }
    seen.add(tag.parentId);
    map[tag.parentId].push(face);
  });
  return map;
}

const EXTRACTED_FACE: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "EXTRACTED_FACE",
};

/**
 * @deprecated
 */
function extractWallFacesByBound(
  parents: BuildingGeomParent[],
  source: WallFaces,
  bottomBoundZ: WorldInches,
  topBoundZ: WorldInches,
  faces?: { [K in keyof WallFaces]?: boolean },
): WallFaces {
  // TODO(Andrew): get all faces that match a certain id

  const tagExtracted = [...parents, EXTRACTED_FACE];

  const wallFaces = wallFacesNew();
  for (const key in source) {
    const faceType = key as keyof WallFaces;
    if (faces && !faces[faceType]) {
      continue;
    }
    for (const face of source[faceType]) {
      const aabb = faceAABB3(face);
      const faceZ = worldInchesNew(aabb.minZ + (aabb.maxZ - aabb.minZ) / 2);
      if (!!face && bottomBoundZ <= faceZ && faceZ < topBoundZ) {
        wallFaces[key].push(tagFace(faceCopy(face), tagExtracted));
      }
    }
  }
  return wallFaces;
}

/**
 * @deprecated
 */
export function wallMassSectionFaces(
  wall: WallMass,
  wallFaces: WallFaces,
  wallSection: WallSection,
  faces?: { [K in keyof WallFaces]?: boolean },
): WallFaces {
  const { bottomOffset, topOffset } = wallSection;
  const bottomZ = worldInchesNew(wall.floorElevation + bottomOffset);
  const topZ = worldInchesNew(wall.floorElevation + topOffset);
  // TODO(Andrew): check to see if the wall Face has the same id as the wall mass
  return extractWallFacesByBound(
    [{ type: "PARENT", parentType: wall.entityType, parentId: wall.id }],
    wallFaces,
    bottomZ,
    topZ,
    faces,
  );
}

interface RoomFaces {
  [roomId: string]: Face[];
}

/**
 * Gathers a WallLayer's finish faces, grouped by Room id
 */

export function wallLayerFacesByRoom(
  layer: SolidWallLayer,
  out: RoomFaces,
  wallLayerType = WallLayerType.FINISH,
  lod: WallLOD = WallLOD.L1,
): void {
  if (layer.wallLayerType !== wallLayerType) {
    return;
  }
  const { leftRoom, rightRoom } = layer.path;

  const intLeft = roomProducesInteriorFaces(leftRoom);
  const intRight = roomProducesInteriorFaces(rightRoom);

  if (!intLeft && !intRight) {
    return;
  }

  const faces = wallFacesFromWallMass(layer, wallLayerType, lod);
  const { leftFaces, rightFaces } = faces;
  if (!!leftRoom && intLeft) {
    for (const face of leftFaces) {
      addFaceByRoomId(out, face, leftRoom.id);
    }
  }
  if (!!rightRoom && intRight) {
    for (const face of rightFaces) {
      addFaceByRoomId(out, face, rightRoom.id);
    }
  }
}
function addFaceByRoomId(acc: RoomFaces, face: Face, roomId: string): RoomFaces {
  if (!acc[roomId]) {
    acc[roomId] = [];
  }
  acc[roomId].push(face);
  return acc;
}
